/**
 * Form validation control type.
 */
type TimfValidationControl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

/**
 * Front form enhancer.
 */
export class FrontApp {
	/**
	 * Invalid focus timer map.
	 *
	 * @var WeakMap<HTMLFormElement, number>
	 */
	private readonly invalidFocusTimers: WeakMap<HTMLFormElement, number> = new WeakMap();

	/**
	 * 初期化を行う.
	 *
	 * @return void
	 */
	public init(): void {
		const forms = document.querySelectorAll<HTMLInputElement>( 'input[name="tomoe_mail_action"]' );

		forms.forEach( ( input: HTMLInputElement ) => {
			const form = input.closest( 'form' );

			if ( ! form ) {
				return;
			}

			form.classList.add( 'c-timf-form' );

			const error = form.querySelector<HTMLElement>( '.timf-form__error' );

			if ( error ) {
				error.classList.add( 'c-timf-form__error' );
			}

			form.querySelectorAll<HTMLElement>( '.timf-item' ).forEach( ( item: HTMLElement ) => {
				item.classList.add( 'c-timf-form__item' );
			} );

			form.querySelectorAll<HTMLElement>( '.timf-item__notice' ).forEach( ( notice: HTMLElement ) => {
				notice.classList.add( 'c-timf-form__notice' );
			} );

			form.querySelectorAll<HTMLElement>( '.timf-item__error' ).forEach( ( fieldError: HTMLElement ) => {
				fieldError.classList.add( 'c-timf-form__field-error' );
			} );

			form.querySelectorAll<HTMLElement>( '.timf-mark--required' ).forEach( ( required: HTMLElement ) => {
				required.classList.add( 'c-timf-form__required' );
			} );

			form.querySelectorAll<HTMLInputElement>( 'input:not([type="checkbox"]):not([type="radio"]):not([type="hidden"]):not([type="submit"]):not([type="button"])' ).forEach( ( element: HTMLInputElement ) => {
				element.classList.add( 'c-timf-form__input' );
			} );

			form.querySelectorAll<HTMLTextAreaElement>( 'textarea' ).forEach( ( element: HTMLTextAreaElement ) => {
				element.classList.add( 'c-timf-form__textarea' );
			} );

			form.querySelectorAll<HTMLSelectElement>( 'select' ).forEach( ( element: HTMLSelectElement ) => {
				element.classList.add( 'c-timf-form__select' );
			} );

			this.bindKanaInputs( form );
			this.bindEmailConfirmationInputs( form );
			this.bindNativeValidationMessages( form );
		} );
	}

	/**
	 * ブラウザの制約バリデーション結果をフォーム内メッセージとして表示する.
	 *
	 * required / type=email / pattern などでブラウザが送信を止めた場合でも、
	 * テーマ上に `.timf-item__error` として視認できるようにする。
	 *
	 * @param form 対象フォーム要素.
	 * @return void
	 */
	private bindNativeValidationMessages( form: HTMLFormElement ): void {
		if ( 'true' === form.dataset.timfValidationBound ) {
			return;
		}

		form.dataset.timfValidationBound = 'true';

		form.addEventListener(
			'invalid',
			( event: Event ) => {
				const control = this.getValidationControl( event.target );

				if ( ! control || control.form !== form ) {
					return;
				}

				event.preventDefault();
				this.showValidationError( control );
				this.queueFirstInvalidFocus( form );
			},
			true
		);

		form.addEventListener( 'input', ( event: Event ) => {
			this.clearValidationErrorIfValid( event.target );
		} );

		form.addEventListener( 'change', ( event: Event ) => {
			this.clearValidationErrorIfValid( event.target );
		} );
	}

	/**
	 * バリデーション対象フォームコントロールを取得する.
	 *
	 * @param target イベント発生元.
	 * @return 対象コントロール。対象外の場合は null。
	 */
	private getValidationControl( target: EventTarget | null ): TimfValidationControl | null {
		if ( target instanceof HTMLInputElement ) {
			if ( [ 'hidden', 'submit', 'button', 'reset' ].includes( target.type ) ) {
				return null;
			}

			return target;
		}

		if ( target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement ) {
			return target;
		}

		return null;
	}

	/**
	 * バリデーションエラーを表示する.
	 *
	 * @param control 対象コントロール.
	 * @return void
	 */
	private showValidationError( control: TimfValidationControl ): void {
		const message = control.validationMessage;

		if ( '' === message ) {
			return;
		}

		const errorElement = this.findOrCreateValidationErrorElement( control );

		if ( ! errorElement ) {
			return;
		}

		errorElement.textContent = message;
		errorElement.hidden = false;
		errorElement.setAttribute( 'role', 'alert' );
		errorElement.classList.add( 'c-timf-form__field-error' );

		this.addControlErrorReference( control, errorElement );
		control.setAttribute( 'aria-invalid', 'true' );
	}

	/**
	 * 有効な入力に戻った場合、クライアント側エラーを隠す.
	 *
	 * @param target イベント発生元.
	 * @return void
	 */
	private clearValidationErrorIfValid( target: EventTarget | null ): void {
		const control = this.getValidationControl( target );

		if ( ! control || ! control.validity.valid ) {
			return;
		}

		const errorElement = this.findValidationErrorElement( control );

		if ( errorElement ) {
			errorElement.hidden = true;
		}

		this.removeControlErrorReference( control, errorElement );
		control.removeAttribute( 'aria-invalid' );
	}

	/**
	 * 最初の無効フィールドへフォーカスする処理を予約する.
	 *
	 * @param form 対象フォーム要素.
	 * @return void
	 */
	private queueFirstInvalidFocus( form: HTMLFormElement ): void {
		if ( this.invalidFocusTimers.has( form ) ) {
			return;
		}

		const timer = window.setTimeout( () => {
			this.invalidFocusTimers.delete( form );

			const firstInvalidControl = this.findFirstInvalidControl( form );

			if ( ! firstInvalidControl ) {
				return;
			}

			firstInvalidControl.focus( { preventScroll: true } );
			firstInvalidControl.scrollIntoView( { block: 'center', behavior: 'smooth' } );
		}, 0 );

		this.invalidFocusTimers.set( form, timer );
	}

	/**
	 * フォーム内の最初の無効フィールドを取得する.
	 *
	 * @param form 対象フォーム要素.
	 * @return 無効フィールド。見つからない場合は null。
	 */
	private findFirstInvalidControl( form: HTMLFormElement ): TimfValidationControl | null {
		const invalidControl = form.querySelector<TimfValidationControl>( 'input:invalid:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]), textarea:invalid, select:invalid' );

		return invalidControl instanceof HTMLInputElement || invalidControl instanceof HTMLTextAreaElement || invalidControl instanceof HTMLSelectElement ? invalidControl : null;
	}

	/**
	 * エラー表示要素を取得、または作成する.
	 *
	 * @param control 対象コントロール.
	 * @return エラー表示要素。配置先が見つからない場合は null。
	 */
	private findOrCreateValidationErrorElement( control: TimfValidationControl ): HTMLElement | null {
		const existing = this.findValidationErrorElement( control );

		if ( existing ) {
			return existing;
		}

		const body = control.closest<HTMLElement>( '.timf-item__body' );

		if ( ! body ) {
			return null;
		}

		const errorElement = document.createElement( 'div' );
		errorElement.className = 'timf-item__error c-timf-form__field-error';
		errorElement.dataset.timfClientError = 'true';
		errorElement.dataset.timfClientErrorFor = this.getControlErrorKey( control );
		errorElement.hidden = true;

		body.appendChild( errorElement );

		return errorElement;
	}

	/**
	 * エラー表示要素を取得する.
	 *
	 * @param control 対象コントロール.
	 * @return エラー表示要素。見つからない場合は null。
	 */
	private findValidationErrorElement( control: TimfValidationControl ): HTMLElement | null {
		if ( control instanceof HTMLInputElement && control.hasAttribute( 'data-timf-email-confirmation-target' ) ) {
			const confirmation = control.closest<HTMLElement>( '.timf-item__confirmation' );
			const confirmationError = confirmation ? confirmation.querySelector<HTMLElement>( '[data-timf-email-confirmation-error]' ) : null;

			if ( confirmationError ) {
				return confirmationError;
			}
		}

		const body = control.closest<HTMLElement>( '.timf-item__body' );

		if ( ! body ) {
			return null;
		}

		const key = this.getControlErrorKey( control );
		const clientErrors = body.querySelectorAll<HTMLElement>( '[data-timf-client-error="true"]' );

		for ( const errorElement of Array.from( clientErrors ) ) {
			if ( key === errorElement.dataset.timfClientErrorFor ) {
				return errorElement;
			}
		}

		return null;
	}

	/**
	 * コントロール単位のエラーキーを取得する.
	 *
	 * @param control 対象コントロール.
	 * @return エラーキー.
	 */
	private getControlErrorKey( control: TimfValidationControl ): string {
		return control.name || control.id || 'timf-field-error';
	}

	/**
	 * コントロールへエラー要素の参照を追加する.
	 *
	 * @param control      対象コントロール.
	 * @param errorElement エラー表示要素.
	 * @return void
	 */
	private addControlErrorReference( control: TimfValidationControl, errorElement: HTMLElement ): void {
		const errorId = this.ensureErrorElementId( control, errorElement );
		const current = control.getAttribute( 'aria-describedby' ) || '';
		const ids = current.split( /\s+/ ).filter( Boolean );

		if ( ! ids.includes( errorId ) ) {
			ids.push( errorId );
		}

		control.setAttribute( 'aria-describedby', ids.join( ' ' ) );
	}

	/**
	 * コントロールからエラー要素の参照を削除する.
	 *
	 * @param control      対象コントロール.
	 * @param errorElement エラー表示要素。存在しない場合は何もしない。
	 * @return void
	 */
	private removeControlErrorReference( control: TimfValidationControl, errorElement: HTMLElement | null ): void {
		if ( ! errorElement || ! errorElement.id ) {
			return;
		}

		const current = control.getAttribute( 'aria-describedby' ) || '';
		const ids = current.split( /\s+/ ).filter( ( id: string ): boolean => Boolean( id ) && id !== errorElement.id );

		if ( 0 < ids.length ) {
			control.setAttribute( 'aria-describedby', ids.join( ' ' ) );
			return;
		}

		control.removeAttribute( 'aria-describedby' );
	}

	/**
	 * エラー表示要素IDを保証する.
	 *
	 * @param control      対象コントロール.
	 * @param errorElement エラー表示要素.
	 * @return エラー表示要素ID.
	 */
	private ensureErrorElementId( control: TimfValidationControl, errorElement: HTMLElement ): string {
		if ( '' !== errorElement.id ) {
			return errorElement.id;
		}

		const key = this.getControlErrorKey( control );
		errorElement.id = this.sanitizeDomId( key ) + '-error';

		return errorElement.id;
	}

	/**
	 * DOM ID として使用できる文字列へ変換する.
	 *
	 * @param value 変換前の値.
	 * @return DOM ID 用文字列.
	 */
	private sanitizeDomId( value: string ): string {
		const sanitized = value.replace( /[^a-zA-Z0-9_-]+/g, '-' ).replace( /^-+|-+$/g, '' );

		return '' !== sanitized ? sanitized : 'timf-field';
	}

	/**
	 * フリガナ入力欄へ全角カタカナ正規化イベントを設定する.
	 *
	 * IME 変換中に input.value を書き換えると、ブラウザの未確定文字列と
	 * スクリプトによる正規化結果が競合し、確定時に文字列が重複することがある。
	 * そのため composition 中は正規化せず、compositionend / input / blur の
	 * 安全なタイミングでのみ値を書き換える。
	 *
	 * @param form 対象フォーム要素.
	 * @return void
	 */
	private bindKanaInputs( form: HTMLFormElement ): void {
		const selector = 'input[data-timf-kana="katakana"]';
		const kanaInputs = form.querySelectorAll<HTMLInputElement>( selector );

		kanaInputs.forEach( ( input: HTMLInputElement ) => {
			if ( 'true' === input.dataset.timfKanaBound ) {
				return;
			}

			input.dataset.timfKanaBound = 'true';

			let isComposing = false;

			const normalize = (): void => {
				this.normalizeKatakanaInput( input );
			};

			const normalizeIfNotComposing = ( event: Event ): void => {
				if ( isComposing || this.isComposingInputEvent( event ) ) {
					return;
				}

				normalize();
			};

			input.addEventListener( 'compositionstart', () => {
				isComposing = true;
			} );

			input.addEventListener( 'compositionend', () => {
				isComposing = false;
				window.setTimeout( normalize, 0 );
			} );

			input.addEventListener( 'input', normalizeIfNotComposing );
			input.addEventListener( 'change', normalizeIfNotComposing );
			input.addEventListener( 'blur', normalize );
		} );
	}

	/**
	 * input イベントが IME 変換中か判定する.
	 *
	 * @param event 入力イベント.
	 * @return IME 変換中の場合 true。
	 */
	private isComposingInputEvent( event: Event ): boolean {
		if ( 'undefined' === typeof InputEvent || ! ( event instanceof InputEvent ) ) {
			return false;
		}

		return event.isComposing;
	}

	/**
	 * 入力欄の値を全角カタカナへ正規化する.
	 *
	 * @param input 対象 input 要素.
	 * @return void
	 */
	private normalizeKatakanaInput( input: HTMLInputElement ): void {
		const value = input.value;
		const normalizedValue = this.normalizeKatakana( value );

		if ( value === normalizedValue ) {
			return;
		}

		const selectionStart = input.selectionStart;
		const selectionEnd = input.selectionEnd;
		const selectionDirection = input.selectionDirection;

		input.value = normalizedValue;

		if ( document.activeElement !== input || null === selectionStart || null === selectionEnd ) {
			return;
		}

		const nextSelectionStart = this.normalizeKatakana( value.slice( 0, selectionStart ) ).length;
		const nextSelectionEnd = this.normalizeKatakana( value.slice( 0, selectionEnd ) ).length;

		try {
			input.setSelectionRange( nextSelectionStart, nextSelectionEnd, selectionDirection ?? 'none' );
		} catch {
			// input type の状態により選択範囲を設定できない場合は値の正規化のみ行う。
		}
	}

	/**
	 * メールアドレス再入力欄の一致チェックイベントを設定する.
	 *
	 * @param form 対象フォーム要素.
	 * @return void
	 */
	private bindEmailConfirmationInputs( form: HTMLFormElement ): void {
		const confirmationInputs = form.querySelectorAll<HTMLInputElement>( 'input[data-timf-email-confirmation-target]' );

		confirmationInputs.forEach( ( confirmationInput: HTMLInputElement ) => {
			const targetId = confirmationInput.getAttribute( 'data-timf-email-confirmation-target' );

			if ( ! targetId ) {
				return;
			}

			const sourceInput = document.getElementById( targetId );

			if ( ! ( sourceInput instanceof HTMLInputElement ) || sourceInput.form !== form ) {
				return;
			}

			const container = confirmationInput.closest<HTMLElement>( '.timf-item__confirmation' );
			const errorElement = container ? container.querySelector<HTMLElement>( '[data-timf-email-confirmation-error]' ) : null;

			const validate = (): boolean => {
				const message = confirmationInput.getAttribute( 'data-timf-email-confirmation-message' ) || 'メールアドレスが一致しません。';
				const sourceValue = sourceInput.value.trim();
				const confirmationValue = confirmationInput.value.trim();
				const requiresMatch = '' !== sourceValue || '' !== confirmationValue;
				const isMismatch = requiresMatch && sourceValue !== confirmationValue;

				confirmationInput.setCustomValidity( isMismatch ? message : '' );

				if ( errorElement ) {
					errorElement.textContent = message;
					errorElement.hidden = ! isMismatch;
				}

				return ! isMismatch;
			};

			sourceInput.addEventListener( 'input', validate );
			confirmationInput.addEventListener( 'input', validate );
			confirmationInput.addEventListener( 'blur', validate );

			form.addEventListener( 'submit', ( event: SubmitEvent ) => {
				if ( validate() ) {
					return;
				}

				event.preventDefault();
				this.showValidationError( confirmationInput );
				this.queueFirstInvalidFocus( form );
			} );
		} );
	}

	/**
	 * 入力値を全角カタカナへ正規化する.
	 *
	 * @param value 入力値.
	 * @return 正規化後の入力値.
	 */
	private normalizeKatakana( value: string ): string {
		return value
			.normalize( 'NFKC' )
			.replace( /[\u3041-\u3096]/g, ( char: string ): string => String.fromCharCode( char.charCodeAt( 0 ) + 0x60 ) );
	}
}
